import React, { useLayoutEffect, useState } from "react";
import styles from "./style.module.scss";
import { useStartYourFreeTrialModel } from "./indexModel";
import coachnow_logo from "app/images/Logo_dark.svg";
import PriceCard from "./_components/PriceCard";
import BillingInformation from "./_components/BillingInformation";
import CheckoutLegalLinks from "app/components/CheckoutLegalLinks";
import price_tag from "app/images/green_price_tag.svg";
import { Toast } from "app/components/helpers";
import useQuery from "app/utils/hooks/useQuery";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { cls } from "app/utils";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import { Authenticated } from "app/components/Wrappers";
import { formatPrice } from "app/utils/currency";
import { useActions } from "app/utils/hooks";
import { resolvePromoOffer } from "app/actions/promoOffer";
import PromoResolveError from "app/components/PromoResolveError";

const PROMO_CODE = "pga6";
const PLAN_CODE = "coachpro+yearly";

const StartYourFreeTrial = () => {
  const { t } = useStartYourFreeTrialModel();
  const history = useHistory();
  const { self } = useSelector((state: RootState) => state.user);
  const promoOffer = useSelector((state: RootState) => state.promoOffer);
  const actions = useActions({ resolvePromoOffer });
  // Trust baseAmount only when the shared resolution is actually for this page's
  // offer (PGA6 pro-yearly) — the store is reused across checkout routes, so a
  // leftover resolution for another code/plan must not leak its price here.
  const isCurrent =
    (promoOffer.code || "").toUpperCase() === PROMO_CODE.toUpperCase() &&
    promoOffer.planCode === PLAN_CODE &&
    promoOffer.currency === "usd";
  const price =
    isCurrent && promoOffer.baseAmount != null
      ? formatPrice(promoOffer.baseAmount, "usd")
      : null;
  // A failed resolve (transport error) leaves no price; surface a retry instead
  // of a perpetual loader. The initial resolve is dispatched by the form below.
  const resolveError = isCurrent && promoOffer.error;
  const retryResolve = () =>
    actions.resolvePromoOffer({ code: PROMO_CODE, planCode: PLAN_CODE, currency: "usd" });
  const [toastAlert, setToastAlert] = useState(null);
  const query = useQuery();
  const location = useLocation();

  useLayoutEffect(() => {
    if (query.get("promo") !== "pga6") {
      history.push("/404");
    }
  }, [query, history, self]);

  const onDone = () => {
    setToastAlert(t("Your account was successfully updated."));
  };

  const hasActiveTrial = self?.subscription?.trialEndsAt >= new Date();

  if (
    hasActiveTrial ||
    self?.subscription?.status === "active" ||
    self?.subscription?.status === "trial" ||
    self?.hasActiveSubscription ||
    self?.hasCoachPlus ||
    self?.hasTrialSubscription
  ) {
    return <Redirect to="/feed" />;
  }

  return (
    <Authenticated redirect="/signup">
      <div
        className={cls(styles.container, "start-pga-trial", {
          [styles.page_background]: location.pathname.includes("signup"),
        })}
      >
        <img
          className={styles.site_logo}
          src={coachnow_logo}
          alt="CoachNow Icon"
        />
        <div className={styles.content_container}>
          <div className={styles.header}>
            <h3 className={styles.header_title}>
              {t("Start your Free Trial")}
            </h3>
            <p className={styles.header_description}>
              {t("To get started enter your card details")}
            </p>
          </div>
          <div className={styles.promocode_applied}>
            <img
              src={price_tag}
              alt="coupon tag"
              className={styles.price_tag}
            />
            <div className={styles.promo_applied_wrapper}>
              <p className={styles.pga_promo_applied}>
                {t("PGA Show Offer Applied")}:
              </p>
              <p className={styles.coupon_description}>
                {t("You’ll get a 7-Day Free Trial")}
              </p>
            </div>
          </div>
          {resolveError ? (
            <PromoResolveError onRetry={retryResolve} />
          ) : (
            <PriceCard price={price} loading={price == null} />
          )}
          <BillingInformation onDone={onDone} />
          <CheckoutLegalLinks />
        </div>

        <Toast success={toastAlert} onDone={() => setToastAlert(null)} />
      </div>
    </Authenticated>
  );
};

export default StartYourFreeTrial;
